import React from 'react';
import { AlertCircle, ArrowDown, ArrowUp, ChevronsUpDown, X } from 'lucide-react';
import { DatePicker } from '../../../components/DatePicker';
import { InlineLoadingState, LoadingSpinner } from '../../../components/LoadingSpinner';
import { useEscapeClose } from '../../../hooks/useEscapeClose';
import { formatIbanDisplay } from '../../../utils/iban';
import { SETTINGS_PATH_BUSINESS } from '../../../utils/settingsRoutes';
import { UI_Z_INDEX } from '../../../utils/uiZIndex';

type SepaSortColumn = 'recipient' | 'iban' | 'remittance' | 'due_date' | 'amount';

function SepaSortableHeader({
  label,
  column,
  activeColumn,
  dir,
  onSort,
  align = 'left',
}: {
  label: string;
  column: SepaSortColumn;
  activeColumn: SepaSortColumn;
  dir: 'asc' | 'desc';
  onSort: (column: SepaSortColumn) => void;
  align?: 'left' | 'right';
}) {
  const active = activeColumn === column;
  const Icon = !active ? ChevronsUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th
      scope="col"
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={`sticky top-0 z-[1] border-b border-app-border bg-app-surface px-4 py-2.5 align-middle whitespace-nowrap first:pl-5 last:pr-5 ${
        align === 'right' ? 'text-right' : 'text-left'
      }`}
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        className={`group/sort inline-flex max-w-full items-center gap-1 text-xs font-semibold transition-colors ${
          active ? 'text-app-text' : 'text-app-text-subtle hover:text-app-text'
        } ${align === 'right' ? 'ml-auto justify-end' : ''}`}
      >
        <span className="truncate">{label}</span>
        <Icon
          className={`h-3.5 w-3.5 shrink-0 transition-opacity ${
            active ? 'opacity-100' : 'opacity-0 group-hover/sort:opacity-60'
          }`}
          aria-hidden
        />
      </button>
    </th>
  );
}

function compareSepaValues(a: string | number | null, b: string | number | null, dir: 'asc' | 'desc'): number {
  const emptyA = a == null || a === '';
  const emptyB = b == null || b === '';
  if (emptyA && emptyB) return 0;
  if (emptyA) return 1;
  if (emptyB) return -1;
  const base = typeof a === 'number' && typeof b === 'number'
    ? a - b
    : String(a).localeCompare(String(b), 'de', { numeric: true, sensitivity: 'base' });
  return dir === 'asc' ? base : -base;
}

export type IncomingSepaPreviewItem = {
  id: string;
  party_name: string | null;
  invoice_number: string | null;
  due_date: string | null;
  /** Überweisungsbetrag, ggf. abzüglich Skonto. */
  amount: number;
  gross_amount: number;
  skonto_applied: number;
  skonto_due_date: string | null;
  currency: string;
  iban: string | null;
  /** Empfängername in der SEPA-Datei (Kontoinhaber, sonst Lieferant). */
  payee_name: string | null;
  remittance: string;
  valid: boolean;
  reason: string | null;
};

export type IncomingSepaPreview = {
  company: { name: string; iban: string | null; bic: string | null; hasIban: boolean };
  items: IncomingSepaPreviewItem[];
};

function recipientName(item: IncomingSepaPreviewItem): string | null {
  return item.payee_name?.trim() || item.party_name;
}

/** Lieferant des Belegs, wenn das Geld an jemand anderen geht (z. B. Auslage eines Monteurs). */
function differingSupplier(item: IncomingSepaPreviewItem): string | null {
  const supplier = item.party_name?.trim();
  const recipient = recipientName(item)?.trim();
  if (!supplier || !recipient) return null;
  return supplier.localeCompare(recipient, 'de', { sensitivity: 'base' }) === 0 ? null : supplier;
}

function euro(value: number): string {
  return value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return value;
  return `${match[3]}.${match[2]}.${match[1]}`;
}

export function IncomingSepaPreviewModal({
  instanceId,
  preview,
  loading,
  exporting,
  error,
  executionDate,
  onExecutionDateChange,
  onClose,
  onExport,
}: {
  instanceId: string;
  preview: IncomingSepaPreview | null;
  loading: boolean;
  exporting: boolean;
  error: string | null;
  executionDate: string;
  onExecutionDateChange: (value: string) => void;
  onClose: () => void;
  onExport: () => void;
}) {
  const [sort, setSort] = React.useState<SepaSortColumn>('due_date');
  const [dir, setDir] = React.useState<'asc' | 'desc'>('asc');
  useEscapeClose(onClose, !exporting);

  const validItems = preview?.items.filter((item) => item.valid) ?? [];
  const validSum = validItems.reduce((sum, item) => sum + item.amount, 0);
  const validSkonto = validItems.reduce((sum, item) => sum + item.skonto_applied, 0);
  const canExport = Boolean(preview?.company.hasIban && validItems.length > 0 && !loading && !exporting);
  const settingsHref = `/i/${encodeURIComponent(instanceId)}${SETTINGS_PATH_BUSINESS}`;

  const sortedItems = React.useMemo(() => {
    const items = preview?.items ?? [];
    return [...items].sort((left, right) => {
      const value = (item: IncomingSepaPreviewItem): string | number | null => {
        if (sort === 'recipient') return recipientName(item);
        if (sort === 'iban') return item.iban;
        if (sort === 'remittance') return item.remittance || item.invoice_number;
        if (sort === 'due_date') return item.due_date;
        return item.amount;
      };
      return compareSepaValues(value(left), value(right), dir);
    });
  }, [preview?.items, sort, dir]);

  const toggleSort = (column: SepaSortColumn) => {
    if (sort === column) setDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    else {
      setSort(column);
      setDir('desc');
    }
  };

  const paymentLabel = validItems.length === 1 ? '1 Zahlung' : `${validItems.length.toLocaleString('de-DE')} Zahlungen`;

  return (
    <div
      className="fixed inset-0 z-[260] flex items-center justify-center bg-slate-900/45 p-4"
      role="dialog"
      aria-modal="true"
      onClick={(event) => {
        if (event.target === event.currentTarget && !exporting) onClose();
      }}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl border border-app-border bg-app-surface shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-app-border bg-app-surface-muted px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-app-text">SEPA-Datei erstellen</h2>
            <p className="mt-0.5 text-xs text-app-text-subtle">Prüfe die Zahlungen und lade eine pain.001-Datei für dein Online-Banking herunter.</p>
          </div>
          <button type="button" onClick={onClose} className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-400 hover:bg-slate-200 hover:text-app-text-muted">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="app-scrollbar min-h-0 flex-1 overflow-y-auto">
          {(preview && !preview.company.hasIban) || error ? (
            <div className="space-y-2 px-5 pt-4">
            {preview && !preview.company.hasIban ? (
              <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  Bitte zuerst die Firmen-IBAN unter{' '}
                  <a href={settingsHref} className="font-medium underline">
                    Einstellungen → Unternehmensangaben
                  </a>{' '}
                  hinterlegen.
                </span>
              </div>
            ) : null}
            {error ? (
              <div className="flex gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            ) : null}
            </div>
          ) : null}

          {loading || !preview ? (
            <InlineLoadingState label="Vorschau wird geladen …" className="py-10" />
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr>
                  <SepaSortableHeader label="Empfänger" column="recipient" activeColumn={sort} dir={dir} onSort={toggleSort} />
                  <SepaSortableHeader label="IBAN" column="iban" activeColumn={sort} dir={dir} onSort={toggleSort} />
                  <SepaSortableHeader label="Verwendungszweck" column="remittance" activeColumn={sort} dir={dir} onSort={toggleSort} />
                  <SepaSortableHeader label="Fällig" column="due_date" activeColumn={sort} dir={dir} onSort={toggleSort} />
                  <SepaSortableHeader label="Betrag" column="amount" activeColumn={sort} dir={dir} onSort={toggleSort} align="right" />
                </tr>
              </thead>
              <tbody className="divide-y divide-app-border">
                {sortedItems.map((item) => {
                  const supplier = differingSupplier(item);
                  return (
                  <tr key={item.id} className={item.valid ? 'bg-app-surface' : 'bg-app-surface-muted text-slate-400'}>
                    <td className="py-2.5 pl-5 pr-4">
                      <div className="font-medium text-app-text">{recipientName(item) || '—'}</div>
                      {supplier ? <div className="mt-0.5 text-[11px] text-app-text-subtle">Beleg: {supplier}</div> : null}
                      {!item.valid && item.reason ? <div className="mt-0.5 text-[11px] text-rose-600">{item.reason}</div> : null}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 font-mono text-xs text-app-text-muted">{item.iban ? formatIbanDisplay(item.iban) : '—'}</td>
                    <td className="px-4 py-2.5 text-app-text-muted">{item.remittance || item.invoice_number || '—'}</td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-app-text-muted">{formatDate(item.due_date)}</td>
                    <td className="whitespace-nowrap py-2.5 pl-4 pr-5 text-right tabular-nums text-app-text">
                      {euro(item.amount)}
                      {item.skonto_applied > 0 ? (
                        <div className="mt-0.5 text-[11px] text-emerald-700 dark:text-emerald-400">
                          {euro(item.gross_amount)} − {euro(item.skonto_applied)} Skonto
                        </div>
                      ) : item.skonto_due_date ? (
                        <div className="mt-0.5 text-[11px] text-app-text-subtle">
                          Skontofrist {formatDate(item.skonto_due_date)} abgelaufen
                        </div>
                      ) : null}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-t border-app-border bg-app-surface-muted px-5 py-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-app-text-muted">Ausführungsdatum</span>
            <div className="w-40">
              <DatePicker
                value={executionDate}
                onChange={onExecutionDateChange}
                className="w-full"
                menuZIndex={UI_Z_INDEX.portalDropdownInModal}
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <div className="text-right">
              <p className="text-[11px] text-app-text-subtle">
                Summe · {paymentLabel}
                {validSkonto > 0 ? (
                  <span className="text-emerald-700 dark:text-emerald-400"> · abzgl. {euro(validSkonto)} Skonto</span>
                ) : null}
              </p>
              <p className="text-base font-semibold leading-tight tabular-nums text-app-text">{euro(validSum)}</p>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={onClose} className="inline-flex h-9 items-center rounded-md border border-app-border bg-app-surface px-3.5 text-sm font-medium text-app-text-muted hover:bg-app-hover">
                Abbrechen
              </button>
              <button
                type="button"
                onClick={onExport}
                disabled={!canExport}
                className="inline-flex h-9 items-center gap-2 rounded-md bg-blue-600 px-3.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {exporting ? <LoadingSpinner size="sm" /> : null}
                SEPA-Datei herunterladen
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
